import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import {
  Package,
  MapPin,
  ClipboardList,
  Box,
  Truck,
  ScanLine,
  ChevronRight,
  Clock,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { useMobile } from '../context/MobileContext';
import {
  receivingAPI,
  pickingAPI,
  packingAPI
} from '@/shared/services/api';

const MobileDashboard = () => {
  const navigate = useNavigate();
  const { currentTask, scanHistory, isOnline } = useMobile();

  const [stats, setStats] = React.useState({
    receiving: { pending: 0, completed: 0 },
    putaway: { pending: 0, completed: 0 },
    picking: { pending: 0, completed: 0 },
    packing: { pending: 0, completed: 0 },
    loadout: { pending: 0, completed: 0 }
  });

  React.useEffect(() => {
    const fetchStats = async () => {
      try {
        const [rcvStats, pickStats, packStats] = await Promise.all([
          receivingAPI.getStats(),
          pickingAPI.getStats(),
          packingAPI.getStats()
        ]);

        setStats({
          receiving: { pending: rcvStats.pending_asns || 0, completed: rcvStats.received_today || 0 },
          putaway: { pending: rcvStats.pending_putaway || 0, completed: rcvStats.putaway_today || 0 },
          picking: { pending: pickStats.pending_picks || 0, completed: pickStats.picked_today || 0 },
          packing: { pending: packStats.pending_packing || 0, completed: packStats.packed_today || 0 },
          loadout: { pending: 0, completed: 0 } // Placeholder for dispatch stats
        });
      } catch (err) {
        console.error('Failed to fetch mobile dashboard stats:', err);
      }
    };
    fetchStats();
  }, []);

  const modules = [
    {
      id: 'receiving',
      name: 'Receiving',
      description: 'Scan NF-e/ASN and receive items',
      icon: Package,
      href: '/mobile/receiving',
      color: 'bg-black',
      stats: stats.receiving
    },
    {
      id: 'putaway',
      name: 'Put-away',
      description: 'Scan pallets to bin locations',
      icon: MapPin,
      href: '/mobile/putaway',
      color: 'bg-green-500',
      stats: stats.putaway
    },
    {
      id: 'picking',
      name: 'Picking',
      description: 'Pick items for orders',
      icon: ClipboardList,
      href: '/mobile/picking',
      color: 'bg-black',
      stats: stats.picking
    },
    {
      id: 'packing',
      name: 'Packing',
      description: 'Pack items into cartons',
      icon: Box,
      href: '/mobile/packing',
      color: 'bg-green-500',
      stats: stats.packing
    },
    {
      id: 'loadout',
      name: 'Load-out',
      description: 'Load cartons to routes',
      icon: Truck,
      href: '/mobile/loadout',
      color: 'bg-black',
      stats: stats.loadout
    }
  ];

  return (
    <div className="p-4 space-y-6" data-testid="mobile-dashboard">
      {/* Active Task Banner */}
      {currentTask && (
        <Card className="border-2 border-primary bg-primary/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <Badge className="mb-2">Active Task</Badge>
                <h3 className="font-semibold">{currentTask.name}</h3>
                <p className="text-sm text-muted-foreground">{currentTask.description}</p>
              </div>
              <Button onClick={() => navigate(currentTask.href)}>
                Continue
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Stats - Uber style */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="border-0 shadow-sm bg-gray-50">
          <CardContent className="p-3 text-center">
            <Clock className="h-5 w-5 mx-auto text-black mb-1" />
            <p className="text-2xl font-bold">21</p>
            <p className="text-xs text-gray-500">Pending</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-gray-50">
          <CardContent className="p-3 text-center">
            <CheckCircle2 className="h-5 w-5 mx-auto text-green-500 mb-1" />
            <p className="text-2xl font-bold">51</p>
            <p className="text-xs text-gray-500">Completed</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-gray-50">
          <CardContent className="p-3 text-center">
            <ScanLine className="h-5 w-5 mx-auto text-black mb-1" />
            <p className="text-2xl font-bold">{scanHistory.length}</p>
            <p className="text-xs text-gray-500">Scans Today</p>
          </CardContent>
        </Card>
      </div>

      {/* Module Cards */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold">Operations</h2>
        {modules.map((module) => {
          const Icon = module.icon;
          return (
            <Card
              key={module.id}
              className="cursor-pointer hover:shadow-md transition-shadow active:scale-[0.98]"
              onClick={() => navigate(module.href)}
              data-testid={`module-${module.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl ${module.color} text-white`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold">{module.name}</h3>
                    <p className="text-sm text-muted-foreground">{module.description}</p>
                    <div className="flex gap-3 mt-2">
                      <Badge variant="outline" className="text-xs">
                        <Clock className="h-3 w-3 mr-1" />
                        {module.stats.pending} pending
                      </Badge>
                      <Badge variant="secondary" className="text-xs">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        {module.stats.completed} done
                      </Badge>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Scans */}
      {scanHistory.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Recent Scans</h2>
          <Card>
            <CardContent className="p-0 divide-y">
              {scanHistory.slice(0, 5).map((scan, idx) => (
                <div key={idx} className="p-3 flex items-center gap-3">
                  <ScanLine className={`h-4 w-4 ${scan.result === 'OK' ? 'text-green-500' : 'text-red-500'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-sm truncate">{scan.barcode}</p>
                    <p className="text-xs text-muted-foreground">{scan.context}</p>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {new Date(scan.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default MobileDashboard;
